/* eslint-disable react/no-find-dom-node */

import React from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import { PRO_SUBSCRIPTIONS_BY_TYPEID } from '../constants/Constants'; //eslint-disable-line

// setting global locale
moment.locale(LANGUAGE_CODE || 'en');

export const getNumber = number => new Intl.NumberFormat(LANGUAGE_CODE).format(number);

export const getLocaleDecimalPrice = price =>
  new Intl.NumberFormat(LANGUAGE_CODE, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);

export const getLocalePrice = price =>
  new Intl.NumberFormat(LANGUAGE_CODE, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);

export const getInputValue = component => (component ? ReactDOM.findDOMNode(component).value : null);

export const getInput = component => ReactDOM.findDOMNode(component);

export const isOrganizationAdmin = user => user && user.is_admin && user.organization_active;

export const isAdmin = user => user && user.is_admin;

export const getAdminOrganization = organization => {
  return `${organization.communication_user.first_name} ${organization.communication_user.last_name} ${organization.communication_user.email}`;
};

export const extractError = error => {
  if (error && error.non_field_errors) {
    return error.non_field_errors[0];
  }

  if (error && error.detail) {
    return error.detail;
  }

  if (Array.isArray(error)) {
    return error[0];
  }

  if (!error) return '';

  return error.toString();
};

export const isOrganizationSuspended = organization => organization && organization.is_suspended;

export const isDowngradingSubscription = (subscription, typeId, billingPeriod) => {
  if (subscription.is_demo) {
    return false;
  }
  if (subscription && subscription.period === 'YEAR' && billingPeriod === 'MONTH') {
    return true;
  }
  if (subscription && subscription.type > typeId) {
    return true;
  }
  return false;
};

export const getProrataPrice = (price, renewalPeriod, organization) => {
  const prevBillingDate = moment.utc(organization.previous_billing_date);
  const daysFromPrevious = prevBillingDate.diff(moment.utc(), 'days');
  const totalDays = prevBillingDate.diff(moment.utc(organization.next_billing_date), 'days');

  return daysFromPrevious === 0 ? price : price * (1 - daysFromPrevious / totalDays);
};

export const getFixedPrice = subscription => {
  const subscriptionData = PRO_SUBSCRIPTIONS_BY_TYPEID.get(subscription.type);
  if (subscription.period === 'YEAR') {
    return subscriptionData.price_year;
  }
  return subscriptionData.price;
};

export const getSubscriptionType = subscription => {
  return subscription.credit === -1 ? 'UNLIMITED' : subscription.credit;
};

export const getSubscriptionPeriod = subscription => {
  if (subscription.end) {
    if (subscription.start.isAfter(subscription.end)) return gettext('Canceled');
    return (
      <span className="item-expires">
        {subscription.is_active ? `${gettext('Expires:')} ${subscription.end.format('ll')}` : gettext('Expired')}{' '}
      </span>
    );
  }
  if (subscription.start.isAfter(moment.utc(), 'day')) {
    return gettext('Starts: ') + subscription.start.format('ll');
  }
  return gettext('Renewal: ') + subscription.next_billing_date.format('ll');
};

export const relativeUrl = url => `${window.__CONFIG__.BASE_NAME}/${url}`;

export const shallowEqual = (objA, objB) => {
  if (objA === objB) {
    return true;
  }

  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  // Test for A's keys different from B.
  const bHasOwnProperty = Object.prototype.hasOwnProperty.bind(objB);
  for (let i = 0; i < keysA.length; i++) {
    if (!bHasOwnProperty(keysA[i]) || objA[keysA[i]] !== objB[keysA[i]]) {
      return false;
    }
  }

  return true;
};

export const getCookie = name => {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      // eslint-disable-next-line no-undef
      const cookie = jQuery.trim(cookies[i]);
      // Does this cookie string begin with the name we want?
      if (cookie.substring(0, name.length + 1) === `${name}=`) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

export function reverseKey(dic) {
  return Object.keys(dic).reduce((acc, key) => {
    acc[dic[key].toString()] = key;

    return acc;
  }, {});
}

export function memoizeFunction(f) {
  const cache = {};

  return (...args) => {
    const argsAsKey = args.reduce((acc, k) => acc + k.toString(), ''); // just taking one argument here

    if (cache[argsAsKey]) {
      return cache[argsAsKey];
    }

    const result = f(...args);
    cache[argsAsKey] = result;
    return result;
  };
}
